import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const sendWinnerNotifications = createAsyncThunk(
  'notifications/sendWinnerNotifications',
  async (notificationData, { rejectWithValue }) => {
    try {
      const response = await api.post('/notifications/winner', notificationData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to send notifications'
      );
    }
  }
);

export const fetchEmailTemplates = createAsyncThunk(
  'notifications/fetchEmailTemplates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/notifications/templates');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch email templates'
      );
    }
  }
);

export const saveEmailTemplate = createAsyncThunk(
  'notifications/saveEmailTemplate',
  async (templateData, { rejectWithValue }) => {
    try {
      const response = await api.post('/notifications/templates', templateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to save email template'
      );
    }
  }
);

export const fetchMemberGroups = createAsyncThunk(
  'notifications/fetchMemberGroups',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/notifications/member-groups');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch member groups'
      );
    }
  }
);

export const createMemberGroup = createAsyncThunk(
  'notifications/createMemberGroup',
  async (groupData, { rejectWithValue }) => {
    try {
      const response = await api.post('/notifications/member-groups', groupData);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create member group'
      );
    }
  }
);

export const deleteMemberGroup = createAsyncThunk(
  'notifications/deleteMemberGroup',
  async (groupId, { rejectWithValue }) => {
    try {
      await api.delete(`/notifications/member-groups/${groupId}`);
      return groupId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to delete member group'
      );
    }
  }
);

const initialState = {
  // Email templates
  emailTemplates: [],
  emailTemplatesLoading: false,
  emailTemplatesError: null,
  
  // Member groups
  memberGroups: [],
  memberGroupsLoading: false,
  memberGroupsError: null,
  
  // Notification sending
  sendingNotifications: false,
  sendNotificationsError: null,
  lastNotificationResult: null,
  
  // UI state
  winnerDialogOpen: false,
  selectedWinner: null,
  selectedGiveaway: null,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    // UI actions
    openWinnerDialog: (state, action) => {
      state.winnerDialogOpen = true;
      state.selectedWinner = action.payload.winner;
      state.selectedGiveaway = action.payload.giveaway;
    },
    closeWinnerDialog: (state) => {
      state.winnerDialogOpen = false;
      state.selectedWinner = null;
      state.selectedGiveaway = null;
      state.sendNotificationsError = null;
      state.lastNotificationResult = null;
    },
    clearNotificationErrors: (state) => {
      state.sendNotificationsError = null;
      state.emailTemplatesError = null;
      state.memberGroupsError = null;
    },
    clearLastNotificationResult: (state) => {
      state.lastNotificationResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Send winner notifications
      .addCase(sendWinnerNotifications.pending, (state) => {
        state.sendingNotifications = true;
        state.sendNotificationsError = null;
      })
      .addCase(sendWinnerNotifications.fulfilled, (state, action) => {
        state.sendingNotifications = false;
        state.lastNotificationResult = action.payload;
      })
      .addCase(sendWinnerNotifications.rejected, (state, action) => {
        state.sendingNotifications = false;
        state.sendNotificationsError = action.payload;
      })
      
      // Fetch email templates
      .addCase(fetchEmailTemplates.pending, (state) => {
        state.emailTemplatesLoading = true;
        state.emailTemplatesError = null;
      })
      .addCase(fetchEmailTemplates.fulfilled, (state, action) => {
        state.emailTemplatesLoading = false;
        state.emailTemplates = action.payload;
      })
      .addCase(fetchEmailTemplates.rejected, (state, action) => {
        state.emailTemplatesLoading = false;
        state.emailTemplatesError = action.payload;
      })
      
      // Save email template
      .addCase(saveEmailTemplate.pending, (state) => {
        state.emailTemplatesLoading = true;
        state.emailTemplatesError = null;
      })
      .addCase(saveEmailTemplate.fulfilled, (state, action) => {
        state.emailTemplatesLoading = false;
        // Add or update template in the list
        const existingIndex = state.emailTemplates.findIndex(
          t => t.id === action.payload.template.id
        );
        if (existingIndex >= 0) {
          state.emailTemplates[existingIndex] = action.payload.template;
        } else {
          state.emailTemplates.push(action.payload.template);
        }
      })
      .addCase(saveEmailTemplate.rejected, (state, action) => {
        state.emailTemplatesLoading = false;
        state.emailTemplatesError = action.payload;
      })
      
      // Fetch member groups
      .addCase(fetchMemberGroups.pending, (state) => {
        state.memberGroupsLoading = true;
        state.memberGroupsError = null;
      })
      .addCase(fetchMemberGroups.fulfilled, (state, action) => {
        state.memberGroupsLoading = false;
        state.memberGroups = action.payload;
      })
      .addCase(fetchMemberGroups.rejected, (state, action) => {
        state.memberGroupsLoading = false;
        state.memberGroupsError = action.payload;
      })
      
      // Create member group
      .addCase(createMemberGroup.pending, (state) => {
        state.memberGroupsLoading = true;
        state.memberGroupsError = null;
      })
      .addCase(createMemberGroup.fulfilled, (state, action) => {
        state.memberGroupsLoading = false;
        state.memberGroups.push(action.payload.group);
      })
      .addCase(createMemberGroup.rejected, (state, action) => {
        state.memberGroupsLoading = false;
        state.memberGroupsError = action.payload;
      })
      
      // Delete member group
      .addCase(deleteMemberGroup.pending, (state) => {
        state.memberGroupsLoading = true;
        state.memberGroupsError = null;
      })
      .addCase(deleteMemberGroup.fulfilled, (state, action) => {
        state.memberGroupsLoading = false;
        state.memberGroups = state.memberGroups.filter(
          group => group.id !== action.payload
        );
      })
      .addCase(deleteMemberGroup.rejected, (state, action) => {
        state.memberGroupsLoading = false;
        state.memberGroupsError = action.payload;
      });
  },
});

export const {
  openWinnerDialog,
  closeWinnerDialog,
  clearNotificationErrors,
  clearLastNotificationResult,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;